import React, { useEffect, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import HeaderDialogo from './HeaderDialogo';
import * as Dialog from '@radix-ui/react-dialog';
import { CheckoutPresenter } from '@/features/checkout/CheckoutPresenter';
import { useIsMobile } from '@/hooks/useIsMobile';
import { BP_MD } from '@/styles/breakpoints';

const ResultadoPago = () => {
  console.log('ResultadoPago component mounting...');
  console.log('Initial window.location.href:', window.location.href);

  // --- HOOKS FIRST ---
  const navigate = useNavigate(); // Declare navigate early
  const searchParams = useSearch({ strict: false });
  console.log('Params from useSearch on mount:', searchParams);

  // --- EXTRACT PARAMS NEXT ---
  const {
    status, // Now 'status' is declared
    nombre_curso,
    fecha_curso,
    nombre_participante,
    apellido_participante,
    email_facturacion,
    email_participante,
    monto,
  } = searchParams;

  // --- DERIVED VALUES ---
  const isSuccess = status === 'approved'; // Now this line works
  const statusClassName = isSuccess ? 'status-success' : 'status-failure';

  // --- EFFECT HOOKS ---
  const [fetchedAmount, setFetchedAmount] = useState(null);
  const provider = searchParams?.provider;
  const paymentId = searchParams?.payment_id;
  useEffect(() => {
    console.log('ResultadoPago useEffect triggered.');
    const currentSearchParams = new URLSearchParams(window.location.search);
    console.log(
      'Params from URLSearchParams in useEffect:',
      Object.fromEntries(currentSearchParams.entries()),
    );
    // You could potentially force a state update here if needed, but try to avoid it
  }, []); // Empty dependency array means it runs once after mount

  // Fetch details by provider/payment_id when present
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        if (provider && paymentId) {
          const resp = await fetch(
            `/api/payment-results/${provider}?payment_id=${encodeURIComponent(paymentId)}`,
          );
          if (!resp.ok) return;
          const data = await resp.json();
          if (data?.amount) setFetchedAmount(data.amount);
        }
      } catch {
        // swallow for now; tests focus on happy path
      }
    };
    fetchDetails();
  }, [provider, paymentId]);

  // --- EVENT HANDLERS ---
  // Handler for when Radix requests an open state change (e.g., Esc, overlay click, Dialog.Close)
  const handleOpenChange = (open) => {
    // We only care about the closing action
    if (!open) {
      console.log('Dialog closing, navigating to /');
      try {
        navigate({
          to: '/',
          search: {}, // Clear search params
          replace: true, // Replace history entry
        });
      } catch (navError) {
        console.error('Error during immediate navigation:', navError);
      }
    }
  };

  // --- RENDER LOGIC ---
  console.log(
    'Rendering with status:',
    status,
    'Is Success:',
    isSuccess, // Use the derived variable
  );

  const isMobile = useIsMobile(`(max-width: ${BP_MD}px)`);

  // Mobile: present as a fullscreen page using the same shell as checkout
  if (isMobile) {
    return (
      <CheckoutPresenter
        variant="fullscreen"
        open={true}
        onClose={() => handleOpenChange(false)}
        title="Resultado de pago"
        showBack={false}
      >
        <div className="form-container">
          <div className={`status-header ${statusClassName}`}>
            <img
              src={`/static/images/${isSuccess ? 'ok.svg' : 'error.svg'}`}
              alt="Status"
              className="status-icon"
            />
            <h1 className="status-title">
              {isSuccess ? '¡Pago Exitoso!' : 'Error en el Pago'}
            </h1>
            <p className="status-subtitle">
              {isSuccess
                ? `¡Felicitaciones, ${nombre_participante}! Tu inscripción ha sido confirmada.`
                : `Lo sentimos, ${nombre_participante}. No pudimos procesar tu pago.`}
            </p>
          </div>

          <div className="details-box">
            <h2 className="details-title">
              {isSuccess ? 'Resumen de la transacción' : 'Detalles del intento'}
            </h2>
            <div className="details-content">
              <p>
                <strong>Curso:</strong> {nombre_curso} ({fecha_curso})
              </p>
              <p>
                <strong>
                  {isSuccess ? 'Monto Pagado:' : 'Monto Intentado:'}
                </strong>{' '}
                {monto}
              </p>
              {isSuccess && email_facturacion && (
                <p>
                  <strong>Factura:</strong> Se enviará a {email_facturacion} en
                  breve.
                </p>
              )}
              {fetchedAmount && (
                <p>
                  <strong>Importe confirmado:</strong> {fetchedAmount}
                </p>
              )}
            </div>
          </div>

          {isSuccess ? (
            <div className="next-steps">
              <h2 className="next-steps-title">Próximos pasos:</h2>
              <ul className="next-steps-list">
                <li>
                  Recibirás un correo electrónico de bienvenida en breve con los
                  detalles de acceso.
                </li>
                <li>
                  Asegúrate de revisar tu bandeja de entrada (y la carpeta de
                  spam).
                </li>
              </ul>
            </div>
          ) : (
            <div className="action-guidance">
              <h2 className="action-guidance-title">¿Qué puedes hacer?</h2>
              <ul className="action-guidance-list">
                <li>
                  Verifica los datos de tu método de pago e inténtalo de nuevo.
                </li>
                <li>Considera usar un método de pago diferente.</li>
                <li>
                  Contacta a tu banco si crees que pueden estar bloqueando la
                  transacción.
                </li>
              </ul>
            </div>
          )}

          <div className="button-container">
            <button
              className="BotonFormulario BotonContinuar"
              aria-label={isSuccess ? 'Listo' : 'Volver a inscribirme'}
              onClick={() => handleOpenChange(false)}
            >
              {isSuccess ? '¡Listo!' : 'Volver a inscribirme'}
            </button>
          </div>

          <div className="footer-help">
            ¿Necesitas ayuda?{' '}
            <a
              href="#contacto"
              className="help-link"
              onClick={() => handleOpenChange(false)}
            >
              Contactanos
            </a>
          </div>
        </div>
      </CheckoutPresenter>
    );
  }

  return (
    <div className="ContenedorModal">
      {/* Keep open={true} only if this component should ALWAYS show the dialog */}
      {/* If it's conditionally rendered, manage open state elsewhere */}
      <Dialog.Root
        defaultOpen={true}
        modal={true}
        onOpenChange={handleOpenChange}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="DialogOverlay" />
          <Dialog.Content
            className="DialogContent"
            aria-label="Resultado de pago"
            data-testid="resultado-dialog"
          >
            {/* Radix always points aria-labelledby at its Dialog.Title. Without
                one it references a missing id, which wins over aria-label and
                leaves the dialog with no accessible name at all. */}
            <Dialog.Title
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              Resultado de pago
            </Dialog.Title>
            <HeaderDialogo
              stepNumber={4}
              onClose={() => handleOpenChange(false)}
            />
            <div className="form-container">
              <div className={`status-header ${statusClassName}`}>
                <img
                  src={`/static/images/${isSuccess ? 'ok.svg' : 'error.svg'}`}
                  alt="Status"
                  className="status-icon"
                />
                <Dialog.Title asChild>
                  <h1 className="status-title">
                    {isSuccess ? '¡Pago Exitoso!' : 'Error en el Pago'}
                  </h1>
                </Dialog.Title>
                <Dialog.Description asChild>
                  <p className="status-subtitle">
                    {isSuccess
                      ? `¡Felicitaciones, ${nombre_participante}! Tu inscripción ha sido confirmada.`
                      : `Lo sentimos, ${nombre_participante}. No pudimos procesar tu pago.`}
                  </p>
                </Dialog.Description>
              </div>

              <div className="details-box">
                <h2 className="details-title">
                  {isSuccess
                    ? 'Resumen de la transacción'
                    : 'Detalles del intento'}
                </h2>
                <div className="details-content">
                  <p>
                    <strong>Curso:</strong> {nombre_curso} ({fecha_curso})
                  </p>
                  <p>
                    <strong>
                      {isSuccess ? 'Monto Pagado:' : 'Monto Intentado:'}
                    </strong>{' '}
                    {monto}
                  </p>
                  {isSuccess && email_facturacion && (
                    <p>
                      <strong>Factura:</strong> Se enviará a {email_facturacion}{' '}
                      en breve.
                    </p>
                  )}
                  {fetchedAmount && (
                    <p>
                      <strong>Importe confirmado:</strong> {fetchedAmount}
                    </p>
                  )}
                  {/* Log status directly if needed for debugging */}
                  {/* <p><strong>Status Raw:</strong> {status}</p> */}
                </div>
              </div>

              {isSuccess ? (
                <div className="next-steps">
                  <h2 className="next-steps-title">Próximos pasos:</h2>
                  <ul className="next-steps-list">
                    <li>
                      Recibirás un correo electrónico de bienvenida en breve con
                      los detalles de acceso.
                    </li>
                    <li>
                      Asegúrate de revisar tu bandeja de entrada (y la carpeta
                      de spam).
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="action-guidance">
                  <h2 className="action-guidance-title">¿Qué puedes hacer?</h2>
                  <ul className="action-guidance-list">
                    <li>
                      Verifica los datos de tu método de pago e inténtalo de
                      nuevo.
                    </li>
                    <li>Considera usar un método de pago diferente.</li>
                    <li>
                      Contacta a tu banco si crees que pueden estar bloqueando
                      la transacción.
                    </li>
                  </ul>
                </div>
              )}

              <div className="button-container">
                {isSuccess ? (
                  <Dialog.Close asChild>
                    {/* The Dialog.Close will trigger handleOpenChange */}
                    <button
                      className="BotonFormulario BotonContinuar"
                      aria-label="Listo"
                    >
                      ¡Listo!
                    </button>
                  </Dialog.Close>
                ) : (
                  <>
                    <Dialog.Close asChild>
                      {/* The Dialog.Close will trigger handleOpenChange */}
                      <button className="BotonFormulario BotonContinuar">
                        Reintentar más tarde
                      </button>
                    </Dialog.Close>
                  </>
                )}
              </div>

              <div className="footer-help">
                ¿Necesitas ayuda?{' '}
                {/* Using Dialog.Close here will also navigate home immediately */}
                <Dialog.Close asChild>
                  <a href="#contacto" className="help-link">
                    Contactanos
                  </a>
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default ResultadoPago;
